import firebase from 'firebase/app'
import 'firebase/firestore'
import logScope from 'log-scope'
import store from '@/store/store.js'
import devMode from '@/devMode.js'
import localDatabase from '@/dev/localDatabase.js'

const log = logScope('firestore')
let db

const firestore = {
  isInitialized: false,
  unsubscribes: {},
  db: db,
  defaultListeners: ['coupons', 'partners', 'klarnaPurchases', 'purchases', 'manualPurchases', 'headQ', 'headQFirmware']
}

firestore.init = async () => {
  if (firestore.isInitialized === true) {
    log.error('Firestore is already initialized')
    return
  }
  firestore.isInitialized = true

  if (devMode.localDatabase === true) {
    log.danger('Using local database instead of firestore')
    db = firestore.db = window.db = localDatabase
    return
  }

  db = firestore.db = firebase.firestore()

  // Standard "read all"-listeners
  firestore.defaultListeners.forEach(collection => {
    firestore.addListener(collection, (snapshot) => {
      firestore.update(collection, snapshot)
    })
  })

  // Custom listeners
  await firestore.subscriberListener()

  log.success('Initializing...')
}

firestore.update = (collection, snapshot) => {
  const data = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }))

  store.commit(collection, data)
}

firestore.addListener = (collection, callback) => {
  if (firestore.unsubscribes[collection]) {
    log.danger('Duplicate collection listeners: ' + collection)
    return
  }

  const unsubscribe = db.collection(collection)
    .onSnapshot(callback)

  firestore.unsubscribes[collection] = unsubscribe
}

firestore.removeListener = (collection) => {
  if (!firestore.unsubscribes[collection]) {
    log.danger('No unsubs for collection: ' + collection)
    return
  }

  firestore.unsubscribes[collection]()
  firestore.unsubscribes[collection] = null

  log.success('Unsubscribed for collection: ' + collection)
}

firestore.add = (collection, data) => {
  return db.collection(collection).add(data)
}

firestore.get = (collection, id = null) => {
  return id !== null
    ? db.collection(collection).doc(id).get()
    : db.collection(collection).get()
}

firestore.set = (collection, id, data, options = { merge: true }) => {
  return db.collection(collection)
    .doc(id)
    .set(data, options)
}

firestore.downloadBackup = async () => {
  const collections = [ ...firestore.defaultListeners ]
  const title = `RideQ backup ${(new Date()).toDateString()}`
  let backup = {}

  log.debug('Initializing backup')

  try {
    await Promise.all(collections.map(async collection => {
      let snapshots = await firestore.get(collection)

      backup[collection] = {}

      snapshots.forEach(snapshot => {
        backup[collection][snapshot.id] = snapshot.data()
      })

      log.debug('Backed up: ' + collection)
    }))

    log.debug('Start downloading')

    let dummy = document.createElement('a')
    dummy.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(JSON.stringify(backup)))
    dummy.setAttribute('download', title)

    dummy.style.display = 'none'
    document.body.appendChild(dummy)

    dummy.click()

    document.body.removeChild(dummy)
    dummy = null

    log.success('Backup completed', backup)
  } catch (err) {
    log.error('Could not backup', err)
  }
}

// Custom listeners
firestore.subscriberListener = async () => {
  // Get the summarized report
  const subscriberReportSnapshot = await db.collection('reports')
    .where('type', '==', 'subscribers')
    .get()
  const subscriberReport = subscriberReportSnapshot.docs[0].data()
  store.commit('logDocumentRead', { type: 'reports', quantity: subscriberReportSnapshot.docs.length })

  // Listen for additional subscribers
  firestore.unsubscribes['subscribers'] = db.collection('subscribers')
    .where('timestamp', '>', subscriberReport.timestamp)
    .onSnapshot(snapshot => {
      store.commit('subscribers', [
        ...subscriberReport.subscribers,
        ...snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      ])

      store.commit('logDocumentRead', { type: 'subscribers', quantity: snapshot.docs.length })
    })
}

if (devMode.enableBackupRestore === true) {
  log.danger('BACKUP RESTORE ENABLED')
  firestore.restoreBackup = async (data) => {
    // const data = {
    //   test: {
    //     'psJ3hGEMej3xhCPtCUzb': { 'email': 'mattias@rideq.se', 'timestamp': 1 }
    //   }
    // }
    const collections = Object.keys(data)

    try {
      await Promise.all(collections.map(async (collection) => {
        const ids = Object.keys(data[collection])

        await Promise.all(ids.map(async (id) => {
          log.debug(`Collection ${collection}. ID: ${id}`)
          const dataPoint = data[collection][id]

          return db.collection(collection)
            .doc(id)
            .set(dataPoint, {
              merge: true
            })
        }))
      }))

      log.success('Completed')
    } catch (err) {
      log.error('could not add data', err)
    }
  }
}

if (devMode.globalShortcuts === true) {
  log.danger('Globalizing')

  window.firestore = firestore

  window.dump = async (collection, data) => {
    const dataList = Object.entries(data).map(([_id, s]) => ({ ...s }))

    try {
      await Promise.all(dataList.map(async dataPoint => {
        const timestamp = dataPoint.timestamp
        const snapshots = await firestore.db.collection(collection)
          .where('timestamp', '==', timestamp)
          .get()

        if (snapshots.docs && snapshots.docs.length > 0) {
          const id = snapshots.docs[0].id
          log.debug('Exists, ID:' + id)

          return firestore.set(collection, id, dataPoint)
        }

        return firestore.add(collection, dataPoint)
      }))

      log.success('Updated: ' + collection)
    } catch (err) {
      log.error('Could not update', err)
    }
  }

  window.addProfile = async (profile) => {
    try {
      await firestore.add('headQ', profile)
      log.success('Profile added')
    } catch (err) {
      log.error('Could not add profile', err)
    }
  }
}

export default firestore

/*
Manually add Klarna purchase

firestore.add('klarnaPurchases', {
  'billing_address': {
    'city': 'Söråker',
    'country': 'SE',
    'email': 'dosiseva@gmail.com',
    'family_name': 'Sundberg',
    'given_name': 'Eva',
    'phone': '+46706855931',
    'postal_code': '861 95',
    'region': '',
    'street_address': 'Krigsbyn 527',
    'street_address2': '',
    'title': ''
  },
  'captured_amount': 0,
  'captures': [],
  'couponCode': 'Balans2019',
  'created_at': '2019-12-12T10:04:07.000Z',
  'customer': { 'date_of_birth': '', 'national_identification_number': '' },
  'expires_at': '2020-01-09T00:00:00.000Z',
  'fraud_status': 'ACCEPTED',
  'initial_payment_method': { 'description': 'Card', 'type': 'CARD' },
  'klarna_reference': 'ZPSNZXHD',
  'locale': 'sv-SE',
  'note': 'Via Klarna',
  'order_amount': 247500,
  'order_id': '65af2d42-ebc5-7f3f-b8e4-113ac2bd2514',
  'order_lines': [{ 'name': 'HeadQ', 'product_url': 'https://www.rideq.se/order', 'quantity': 1, 'quantity_unit': '', 'reference': '1', 'tax_rate': 2500, 'total_amount': 247500, 'total_discount_amount': 0, 'total_tax_amount': 49500, 'type': 'physical', 'unit_price': 247500 }],
  'original_order_amount': 247500,
  'partnerCode': '',
  'purchase_country': 'SE',
  'purchase_currency': 'SEK',
  'refunded_amount': 0,
  'refunds': [],
  'remaining_authorized_amount': 247500,
  'shipping_address': {
    'city': 'Söråker',
    'country': 'SE',
    'email': 'dosiseva@gmail.com',
    'family_name': 'Sundberg',
    'given_name': 'Eva',
    'phone': '+46706855931',
    'postal_code': '861 95',
    'region': '',
    'street_address': 'Krigsbyn 527',
    'street_address2': '',
    'title': ''
  },
  'status': 0,
  'timestamp': 1576141440000
})

*/
